import { parseEntityRef } from '@backstage/catalog-model';
import { plural } from '../home/attention';

/**
 * The scorecards as the github-insights backend answers them (DX-77,
 * specs/scorecards.md; how each check is decided is docs/scorecards.md).
 *
 * The page and a service's facts bar both read them from there rather than
 * scoring anything themselves, so the score on a service's page is the one its
 * row on Scorecards shows.
 */

export type CheckId =
  | 'owner'
  | 'techdocs'
  | 'runbook'
  | 'statusProbe'
  | 'api'
  | 'relations'
  | 'mainGreen'
  | 'noCritical'
  // The four rules of the CI/CD standard a service's team fixes in its own
  // repository (DX-225), read from the repository measurement.
  | 'gate'
  | 'deployGated'
  | 'nodePinned'
  | 'lockfile';

export type MarkState = 'pass' | 'fail' | 'unknown';

export type Mark = {
  state: MarkState;
  /** What was found, in a sentence. */
  detail: string;
  /** Where it can be seen, when there is such a place. */
  link?: string;
};

export type ScoredService = {
  entityRef: string;
  name: string;
  title?: string;
  /** The owner it names: a team, a person, or one the catalog does not hold. */
  owner?: { ref: string; title?: string };
  marks: Record<CheckId, Mark>;
  passed: number;
  /** The checks that were asked — passed or failed, not unknown. */
  asked: number;
};

export type SourceState = {
  id: 'docs' | 'status' | 'builds' | 'security' | 'repositories';
  label: string;
  checks: CheckId[];
  state: 'answered' | 'pending' | 'partial' | 'failed';
  unanswered: number;
};

export type Check = { id: CheckId; title: string };

export type ScorecardsSnapshot = {
  /** When the checks last ran; null before they first did. */
  generatedAt: string | null;
  checks: Check[];
  services: ScoredService[];
  summary: {
    services: number;
    readiness: number | null;
    checks: Record<CheckId, { passed: number; asked: number }>;
  };
  sources: SourceState[];
};

/** One service's row, for the Scorecard cell on its own page. */
export type ServiceScorecard = {
  generatedAt: string | null;
  checks: Check[];
  sources: SourceState[];
  /** Null for an entity that is not scored, or not scored yet. */
  service: ScoredService | null;
};

/** The catalog type the checks are asked of. */
export const SCORED_TYPE = 'service';

/** A share as the figures write it: whole percent, never rounded up to 100. */
export function formatShare(part: number, whole: number): string {
  const pct = (part / whole) * 100;
  return `${pct >= 100 ? 100 : Math.min(Math.round(pct), 99)}%`;
}

/** How a score reads at a glance — its colour, never its only carrier. */
export type Tone = 'good' | 'fair' | 'poor';

/** Green from 85 %, red at half or below, amber in between. */
export function toneOf(passed: number, asked: number): Tone {
  if (asked === 0) return 'poor';
  const share = passed / asked;
  if (share >= 0.85) return 'good';
  if (share > 0.5) return 'fair';
  return 'poor';
}

/** The row's anchor on Scorecards, which a service's facts bar links to. */
export function rowIdOf(entityRef: string): string {
  const { namespace, name } = parseEntityRef(entityRef);
  return namespace === 'default'
    ? `scorecard-${name}`
    : `scorecard-${namespace}-${name}`;
}

/** The row of a service on Scorecards. */
export const scorecardLinkOf = (entityRef: string) =>
  `/scorecards#${rowIdOf(entityRef)}`;

/** A service's own page. */
export function entityPageOf(entityRef: string): string {
  const { kind, namespace, name } = parseEntityRef(entityRef);
  return `/catalog/${namespace}/${kind.toLowerCase()}/${name}`;
}

/** Whether a source's label takes a plural verb: "the builds have". */
const PLURAL_LABEL: Record<SourceState['id'], boolean> = {
  docs: false,
  status: false,
  builds: true,
  security: true,
  repositories: false,
};

/**
 * What a source that did not fully answer means for the scores, in one
 * sentence; undefined when it answered.
 */
export function sourceNotice(
  source: SourceState,
  checks: Check[],
): string | undefined {
  const names = source.checks
    .map(id => `“${checks.find(c => c.id === id)?.title ?? id}”`)
    .join(' and ');
  const are = source.checks.length === 1 ? 'is' : 'are';
  const has = PLURAL_LABEL[source.id] ? 'have' : 'has';
  switch (source.state) {
    case 'pending':
      return `${source.label} ${has} not been read since the portal started, so ${names} ${are} not counted yet.`;
    case 'failed':
      return `${source.label} did not answer, so ${names} ${are} not counted for any service.`;
    case 'partial':
      return `${source.label} ${has} no answer yet for ${plural(
        source.unanswered,
        'service',
        'services',
      )}, so ${names} ${are} not counted for ${
        source.unanswered === 1 ? 'it' : 'them'
      }.`;
    default:
      return undefined;
  }
}
